import { useEffect, useRef, useState, useCallback } from 'react'
import { PageHeader, FormField, Alert, btnPrimary, btnSecondary, input, colors } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { useAuth } from '../../hooks/use-auth'

const MIGRATE_POLICIES = ['auto', 'manual', 'abort-on-destructive'] as const
type MigratePolicy = (typeof MIGRATE_POLICIES)[number]

interface UpdateStatus {
  readonly installedVersion: string
  readonly latestAvailable: string | null
  readonly updateAvailable: boolean
  readonly releaseNotesUrl: string | null
  readonly migrationPolicy: MigratePolicy
}

interface RollbackEligibility {
  readonly rollbackAvailable: boolean
  readonly previousVersion: string | null
  readonly previousInstalledAt: string | null
}

interface AgentSuggestion {
  readonly id: string
  readonly kind: string
  readonly title: string
  readonly rationale: string
  readonly severity: 'info' | 'warn' | 'error'
  readonly status: string
  readonly createdAt: string
}

const POLICY_LABELS: Record<MigratePolicy, string> = {
  auto: 'Auto (apply on every update)',
  manual: 'Manual (skip migrate; run by hand)',
  'abort-on-destructive': 'Abort if destructive SQL detected',
}

export function UpdatesPanel() {
  const { user } = useAuth()
  const isMedflowAdmin = user?.role === 'medflow_admin'

  const [status, setStatus] = useState<UpdateStatus | null>(null)
  const [rollback, setRollback] = useState<RollbackEligibility | null>(null)
  const [policy, setPolicy] = useState<MigratePolicy>('auto')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const cancelledRef = useRef(false)

  // Agent suggestions state (medflow_admin only)
  const [suggestionsOpen, setSuggestionsOpen] = useState(false)
  const [suggestions, setSuggestions] = useState<readonly AgentSuggestion[]>([])
  const [suggestionsLoading, setSuggestionsLoading] = useState(false)
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null)
  const [actionMessage, setActionMessage] = useState<string | null>(null)

  useEffect(() => {
    cancelledRef.current = false
    Promise.all([
      api.get<UpdateStatus>('/meta/update-status'),
      api.get<RollbackEligibility>('/meta/rollback-eligibility'),
    ])
      .then(([statusRes, rollbackRes]) => {
        if (cancelledRef.current) return
        if (statusRes.data) {
          setStatus(statusRes.data)
          setPolicy(statusRes.data.migrationPolicy as MigratePolicy)
        }
        if (rollbackRes.data) {
          setRollback(rollbackRes.data)
        }
        setLoading(false)
      })
      .catch((err) => {
        if (cancelledRef.current) return
        setError(err instanceof ApiError ? err.message : 'Failed to load update status')
        setLoading(false)
      })
    return () => {
      cancelledRef.current = true
    }
  }, [])

  useEffect(() => {
    if (!isMedflowAdmin || !suggestionsOpen) return
    setSuggestionsLoading(true)
    setSuggestionsError(null)
    api
      .get<{ rows: AgentSuggestion[] }>('/admin/agent-suggestions?kind=upgrade&status=pending&limit=50')
      .then((res) => {
        setSuggestions(res.data?.rows ?? [])
        setSuggestionsLoading(false)
      })
      .catch((err) => {
        setSuggestionsError(err instanceof ApiError ? err.message : 'Failed to load suggestions')
        setSuggestionsLoading(false)
      })
  }, [isMedflowAdmin, suggestionsOpen])

  const handleSuggestionAction = useCallback(async (id: string, newStatus: 'accepted' | 'dismissed') => {
    setActionMessage(null)
    try {
      await api.patch(`/admin/agent-suggestions/${id}`, { status: newStatus })
      setSuggestions((prev) => prev.filter((s) => s.id !== id))
      if (newStatus === 'accepted') {
        setActionMessage('Accepted. Run `install.sh --version <tag>` to apply.')
      }
    } catch (err) {
      setSuggestionsError(err instanceof ApiError ? err.message : 'Action failed')
    }
  }, [])

  async function handleSavePolicy() {
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      await api.patch('/admin/settings/update-policy', { migratePolicy: policy })
      setSuccess('Migration policy saved.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div style={{ color: colors.textMuted }}>Loading...</div>

  return (
    <div>
      <PageHeader title="Updates" />
      <p style={{ color: colors.textMuted, fontSize: '0.875rem', margin: '0 0 1.25rem' }}>
        Manage version updates and migration behavior for this deployment.
      </p>

      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={success} />}

      {/* Version status */}
      <section style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '0 0 0.75rem', color: colors.text }}>
          Version Status
        </h3>
        <div
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '1rem',
          }}
        >
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginBottom: '0.25rem' }}>Installed</div>
              <div style={{ fontFamily: 'monospace', fontWeight: 600, color: colors.text }}>
                {status?.installedVersion ?? '—'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginBottom: '0.25rem' }}>
                Latest Available
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontFamily: 'monospace', fontWeight: 600, color: colors.text }}>
                  {status?.latestAvailable ?? 'Not advertised'}
                </span>
                {status?.updateAvailable && (
                  <span
                    style={{
                      background: '#d97706',
                      color: '#fff',
                      fontSize: '0.6875rem',
                      fontWeight: 700,
                      padding: '2px 6px',
                      borderRadius: '4px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Update Available
                  </span>
                )}
              </div>
            </div>
          </div>
          {status?.releaseNotesUrl && (
            <div style={{ marginTop: '0.75rem' }}>
              <a
                href={status.releaseNotesUrl}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: '0.875rem', color: colors.primary ?? '#3b82f6' }}
              >
                View release notes
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Rollback status */}
      <section style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '0 0 0.75rem', color: colors.text }}>Rollback</h3>
        <div
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '1rem',
          }}
        >
          {rollback?.rollbackAvailable ? (
            <div>
              <div style={{ color: colors.text, marginBottom: '0.5rem' }}>
                Rollback available to version{' '}
                <strong style={{ fontFamily: 'monospace' }}>{rollback.previousVersion}</strong>
              </div>
              {rollback.previousInstalledAt && (
                <div style={{ fontSize: '0.8125rem', color: colors.textMuted }}>
                  Previously installed: {new Date(rollback.previousInstalledAt).toLocaleString()}
                </div>
              )}
              <p style={{ fontSize: '0.8125rem', color: colors.textMuted, marginTop: '0.5rem' }}>
                To roll back, run <code>./rollback.sh</code> on the host.
              </p>
            </div>
          ) : (
            <p style={{ color: colors.textMuted, margin: 0, fontSize: '0.875rem' }}>
              No rollback available. A rollback target is created on each version install.
            </p>
          )}
        </div>
      </section>

      {/* Migration policy */}
      <section style={{ marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '0 0 0.75rem', color: colors.text }}>
          Migration Policy
        </h3>
        <p style={{ fontSize: '0.8125rem', color: colors.textMuted, margin: '0 0 0.75rem' }}>
          Controls how <code>install.sh</code> handles database migrations during updates.
        </p>
        <FormField name="migratePolicy" label="Policy">
          <select
            id="migratePolicy"
            value={policy}
            onChange={(e) => setPolicy(e.target.value as MigratePolicy)}
            style={{ ...input, width: '24rem' }}
          >
            {MIGRATE_POLICIES.map((p) => (
              <option key={p} value={p}>
                {POLICY_LABELS[p]}
              </option>
            ))}
          </select>
        </FormField>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button type="button" onClick={handleSavePolicy} style={btnPrimary} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </section>

      {/* Agent suggestions (upgrade kind) */}
      <section style={{ marginTop: '1.5rem' }}>
        <button
          type="button"
          onClick={() => setSuggestionsOpen((v) => !v)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            color: colors.text,
            fontSize: '0.875rem',
            fontWeight: 600,
          }}
        >
          <span style={{ fontSize: '0.7rem', color: colors.textMuted }}>{suggestionsOpen ? '▼' : '▶'}</span>
          Agent Suggestions
        </button>

        {suggestionsOpen && (
          <div
            style={{
              marginTop: '0.75rem',
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              padding: '1rem',
            }}
          >
            {isMedflowAdmin ? (
              <>
                {suggestionsError && <Alert type="error" message={suggestionsError} />}
                {actionMessage && (
                  <div
                    style={{
                      background: '#ecfdf5',
                      border: '1px solid #6ee7b7',
                      borderRadius: '4px',
                      padding: '0.5rem 0.75rem',
                      marginBottom: '0.75rem',
                      fontSize: '0.8125rem',
                      color: '#065f46',
                    }}
                  >
                    {actionMessage}
                  </div>
                )}
                {suggestionsLoading ? (
                  <div style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Loading…</div>
                ) : suggestions.length === 0 ? (
                  <p style={{ color: colors.textMuted, margin: 0, fontSize: '0.875rem' }}>
                    No pending upgrade suggestions.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {suggestions.map((s) => (
                      <div
                        key={s.id}
                        style={{
                          border: `1px solid ${colors.border}`,
                          borderRadius: '4px',
                          padding: '0.75rem',
                          background: '#fff',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: '1rem',
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                color: colors.text,
                                marginBottom: '0.25rem',
                              }}
                            >
                              {s.title}
                            </div>
                            <div style={{ fontSize: '0.8125rem', color: colors.textMuted, marginBottom: '0.5rem' }}>
                              {s.rationale}
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                              <span
                                style={{
                                  fontSize: '0.6875rem',
                                  fontWeight: 700,
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  textTransform: 'uppercase',
                                  background:
                                    s.severity === 'error' ? '#fee2e2' : s.severity === 'warn' ? '#fef3c7' : '#e0f2fe',
                                  color:
                                    s.severity === 'error' ? '#991b1b' : s.severity === 'warn' ? '#92400e' : '#0c4a6e',
                                }}
                              >
                                {s.severity}
                              </span>
                              <span style={{ fontSize: '0.75rem', color: colors.textMuted }}>
                                {new Date(s.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                            <button
                              type="button"
                              style={{ ...btnPrimary, fontSize: '0.8125rem', padding: '4px 10px' }}
                              onClick={() => void handleSuggestionAction(s.id, 'accepted')}
                            >
                              Accept
                            </button>
                            <button
                              type="button"
                              style={{ ...btnSecondary, fontSize: '0.8125rem', padding: '4px 10px' }}
                              onClick={() => void handleSuggestionAction(s.id, 'dismissed')}
                            >
                              Dismiss
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p style={{ color: colors.textMuted, margin: 0, fontSize: '0.875rem' }}>
                Upgrade suggestions are pending. Contact your MedFlow administrator to review them.
              </p>
            )}
          </div>
        )}
      </section>
    </div>
  )
}
